import React, { useMemo } from "react";
import { CalendarDays, Compass, Play, Shuffle, SlidersHorizontal, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import HelpLink from "../help/HelpLink";
import { formatLabel } from "../../utils/formatLabel";
import { summariseExercises } from "../../utils/programmeContext";
import { buildSlotGenerationDefaults } from "../../config/periodisation";
import { calculateTotalDuration } from "../../utils/time";
import { builtName, slotName } from "../../utils/programmeSchedule";

const MODE_KICKERS = {
  next: { icon: Compass, text: "What's next" },
  mixed: { icon: Sparkles, text: "Suggested for you" },
};

const NEW_USER_REASON =
  "A first session to see how you move. Khronos tailors the next ones as it learns your training.";
const SURPRISE_REASON = "A session shape you haven't done lately.";

// Where the slot sits in its week and the programme. Only facts the
// programme actually holds: no invented loads or progressions.
const describeProgrammeSlot = (programme, slot) => {
  const week = (programme?.weeks || []).find(
    (w) => w.week_number === slot.week_number,
  );
  const inWeek = week?.sessions || [];
  const position = inWeek.findIndex((s) => s.id === slot.id) + 1;
  const parts = [];
  if (position > 0 && inWeek.length > 1) {
    parts.push(`Session ${position} of ${inWeek.length} this week.`);
  }
  if (programme?.duration_weeks) {
    parts.push(`Week ${slot.week_number} of ${programme.duration_weeks}.`);
  }
  if (slot.plan_data) parts.push("It's planned and ready to run.");
  else parts.push("Start builds it from your programme first, then opens it.");
  return parts.join(" ");
};

const programmeFacts = (programme, slot) => {
  const facts = [];
  if (slot.plan_data) {
    const seconds = calculateTotalDuration(slot.plan_data);
    if (seconds > 0) facts.push(`${Math.round(seconds / 60)} min`);
    const exercises = summariseExercises(slot.plan_data, 3);
    if (exercises) facts.push(exercises);
    return facts;
  }
  const defaults = buildSlotGenerationDefaults(programme, slot) || {};
  if (defaults.sessionDuration) facts.push(`${defaults.sessionDuration} min`);
  (defaults.workoutTypes || []).forEach((t) => facts.push(formatLabel(t)));
  return facts;
};

/**
 * The one suggestion at the top of the Session tab: the next programme
 * session if there is one, otherwise What's next (with history) or Surprise
 * me (new users). Start goes straight to building it; Customise opens the
 * generate screen filled in with it.
 */
const TodayCard = ({
  programme,
  programmeSlot,
  selector,
  preview,
  sessionMinutes,
  isNewUser,
  onStart,
  onShuffle,
  onCustomise,
  onOpenProgramme,
}) => {
  const card = useMemo(() => {
    if (programmeSlot) {
      return {
        icon: CalendarDays,
        kicker: `Your programme · week ${programmeSlot.week_number}`,
        title: slotName(programmeSlot),
        facts: [builtName(programmeSlot), ...programmeFacts(programme, programmeSlot)]
          .filter(Boolean),
        reason: describeProgrammeSlot(programme, programmeSlot),
      };
    }
    const archetype = preview?.archetype;
    const kicker = MODE_KICKERS[selector] || MODE_KICKERS.mixed;
    return {
      icon: kicker.icon,
      kicker: kicker.text,
      title: archetype?.name || "A session for today",
      facts: [
        sessionMinutes ? `${sessionMinutes} min` : null,
        ...(preview?.styles || []).map((s) => formatLabel(s)),
      ].filter(Boolean),
      reason:
        archetype?.reason || (isNewUser ? NEW_USER_REASON : SURPRISE_REASON),
    };
  }, [programme, programmeSlot, selector, preview, sessionMinutes, isNewUser]);

  const Icon = card.icon;
  return (
    <section
      aria-label="Today"
      className="grid gap-2.5 rounded-2xl border border-primary/40 bg-primary/[0.07] p-4"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
        <Icon className="h-3.5 w-3.5" />
        {card.kicker}
        <HelpLink
          topic="todays-suggestion#where-it-comes-from"
          label="Why this suggestion?"
          className="ml-auto text-primary/70 hover:text-primary"
        />
      </div>
      <h3 className="text-xl font-bold leading-tight text-white [text-wrap:balance]">
        {card.title}
      </h3>
      {card.facts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {card.facts.map((fact) => (
            <span
              key={fact}
              className="max-w-full truncate rounded-full border border-border bg-white/5 px-2.5 py-0.5 text-xs text-foreground"
            >
              {fact}
            </span>
          ))}
        </div>
      )}
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        {card.reason}
      </p>
      <div className="mt-0.5 flex gap-2">
        <Button
          type="button"
          onClick={onStart}
          className="h-12 flex-1 rounded-xl text-[15px] font-bold"
        >
          <Play className="mr-2 h-4 w-4 fill-current" />
          Start
        </Button>
        {programmeSlot ? (
          <Button
            type="button"
            variant="outline"
            onClick={onOpenProgramme}
            className="h-12 rounded-xl border-border bg-muted/40 px-4 font-semibold hover:bg-muted/60"
          >
            Programme
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={onShuffle}
              aria-label="Something else"
              title="Something else"
              className="h-12 w-12 rounded-xl border-border bg-muted/40 hover:bg-muted/60"
            >
              <Shuffle className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={onCustomise}
              aria-label="Customise"
              title="Customise"
              className="h-12 w-12 rounded-xl border-border bg-muted/40 hover:bg-muted/60"
            >
              <SlidersHorizontal className="h-5 w-5" />
            </Button>
          </>
        )}
      </div>
    </section>
  );
};

export default TodayCard;
